import { h } from 'preact'
import { useEffect } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { PresenceDot } from './PresenceDot.jsx'
import { LastSeen } from './LastSeen.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'

const CLOSE_DELAY_MS = 220 // matches CSS transition duration

/**
 * ContactProfilePanel — centered modal overlay with fade+scale animation.
 *
 * Controlled by store.profilePerson signal:
 *   null               → modal is hidden (but stays in DOM for exit animation)
 *   { id, name, ... }  → modal is visible and fetches full profile
 *
 * Props: { store }
 */
export function ContactProfilePanel({ store }) {
  const person  = store.profilePerson.value
  // visible drives the CSS is-visible class; displayPerson keeps the last
  // known person in DOM during the exit animation so content doesn't flicker.
  const visible       = useSignal(false)
  const displayPerson = useSignal(null)
  const profile       = useSignal(null)
  const loading       = useSignal(false)
  const sending       = useSignal(false)
  const error         = useSignal(null)
  const closeTimer    = useSignal(null)

  const activeRoomId = store.activeRoomId.value

  // Sync open/close with store.profilePerson
  useEffect(() => {
    if (person) {
      // Cancel any pending close timer
      if (closeTimer.value) { clearTimeout(closeTimer.value); closeTimer.value = null }
      displayPerson.value = person
      // Defer setting visible so the initial CSS state renders first (opacity:0)
      requestAnimationFrame(() => { visible.value = true })
    } else {
      visible.value = false
      // Keep displayPerson until transition ends so content stays visible
      closeTimer.value = setTimeout(() => {
        displayPerson.value = null
        profile.value       = null
        error.value         = null
      }, CLOSE_DELAY_MS)
    }
  }, [person?.id]) // eslint-disable-line

  // Fetch full profile when displayPerson changes (and visible opens)
  useEffect(() => {
    const p = displayPerson.value
    if (!p || !activeRoomId) return
    profile.value = null
    error.value   = null
    loading.value = true

    chatApi.persons.profile(activeRoomId, p.id)
      .then(async res => {
        if (res.ok) {
          profile.value = await res.json
        } else {
          error.value = 'error'
        }
      })
      .catch(() => { error.value = 'error' })
      .finally(() => { loading.value = false })
  }, [displayPerson.value?.id, activeRoomId]) // eslint-disable-line

  // Don't render anything until a person has been set at least once
  if (!displayPerson.value && !visible.value) return null

  function handleClose() {
    store.profilePerson.value = null
  }

  async function handleSendMessage() {
    if (sending.value) return
    sending.value = true
    try {
      const res = await chatApi.directs.open(displayPerson.value.id)
      if (res.ok) {
        const room = await res.json
        const existing = store.rooms.value.find(r => r.id === room.id)
        if (!existing) store.rooms.value = [room, ...store.rooms.value]
        store.profilePerson.value = null
        if (typeof store.navigateToRoom === 'function') {
          store.navigateToRoom(room.id)
        } else {
          store.activeRoomId.value = room.id
        }
      }
    } catch { /* non-fatal */ }
    sending.value = false
  }

  const dp       = displayPerson.value
  const fullData = profile.value || dp
  const avatarUrl = fullData?.avatar_url || null
  const name      = fullData?.name || ''
  const initials  = name.slice(0, 1).toUpperCase() || '?'

  return (
    <div
      class={`chat-profile-panel${visible.value ? ' is-visible' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={name}
    >
      {/* Backdrop */}
      <div class="chat-profile-panel-backdrop" onClick={handleClose} />

      <div class="chat-profile-panel-inner">
        {/* Close button */}
        <button
          type="button"
          class="chat-profile-panel-close"
          onClick={handleClose}
          title={t('chat.profile.close')}
        >
          <i class="bi bi-x-lg" />
        </button>

        {/* Avatar */}
        <div class="chat-profile-avatar-wrap">
          {avatarUrl
            ? <img
                src={avatarUrl}
                alt={name}
                class="chat-profile-avatar"
                onLoad={e => e.target.classList.add('is-loaded')}
                onError={e => { e.target.style.display = 'none' }}
              />
            : <div class="chat-profile-avatar chat-profile-avatar--initials">{initials}</div>
          }
          {dp && (
            <span class="chat-profile-presence-dot">
              <PresenceDot personId={dp.id} store={store} size="md" />
            </span>
          )}
        </div>

        {/* Name + status */}
        <div class="chat-profile-name">{name}</div>
        {dp && (
          <div class="chat-profile-status">
            <LastSeen personId={dp.id} store={store} />
          </div>
        )}

        {/* Details */}
        {loading.value && (
          <div class="chat-profile-loading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          </div>
        )}

        {!loading.value && profile.value && (
          <ul class="chat-profile-details">
            {profile.value.role && (
              <li class="chat-profile-detail-row">
                <i class="bi bi-person-badge chat-profile-detail-icon" />
                <span>{t(`chat.profile.roles.${profile.value.role}`, { defaultValue: profile.value.role })}</span>
              </li>
            )}
            {profile.value.phone_number && (
              <li class="chat-profile-detail-row">
                <i class="bi bi-telephone chat-profile-detail-icon" />
                <a href={`tel:${profile.value.phone_number}`} class="chat-profile-detail-link">
                  {profile.value.phone_number}
                </a>
              </li>
            )}
            {profile.value.email && (
              <li class="chat-profile-detail-row">
                <i class="bi bi-envelope chat-profile-detail-icon" />
                <a href={`mailto:${profile.value.email}`} class="chat-profile-detail-link">
                  {profile.value.email}
                </a>
              </li>
            )}
          </ul>
        )}

        {/* Send message button */}
        <button
          type="button"
          class="chat-profile-send-btn btn btn-primary"
          onClick={handleSendMessage}
          disabled={sending.value}
        >
          {sending.value
            ? <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
            : <i class="bi bi-chat-dots me-1" />
          }
          {t('chat.profile.send_message')}
        </button>
      </div>
    </div>
  )
}
